import React, { useContext, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  ActionButtonHandle,
  AutocompleteValue,
  FormAutocomplete,
  FormContext,
  FormDictionaryField,
  UserContext,
  useScrollableSource,
} from '@eas/common-web';
import { useConcernedAuthorityTypes } from '@modules/dict-concerned-authority-type/dict-concerned-authority-type-api';
import { CONTACTS_SORT, useContacts } from '@modules/subject/subject-api';
import {
  useContactColumns,
  useContactFiltersFields,
} from '@modules/subject/subject-columns';
import { SubjectToolbar } from '@modules/subject/subject-toolbar';
import {
  ContactAutocompleteItem,
  createRowStyle,
} from '@composite/contact/conact-utils';
import { Contact, Subject } from '@models';
import { EvidenceApiUrl, EvidenceBrowserUrl, Role } from '@enums';
import {
  autocompleteLabelMapper,
  dictionaryFieldLabelMapper,
} from '../../components/form/misc/label-mappers';

export function FormFields() {
  const { setFieldValue, getFieldValues } = useContext(FormContext);
  const { hasPermission } = useContext(UserContext);

  const { contactColumns } = useContactColumns({
    labelPrefix: 'Adresát',
  });
  const { contactFiltersFields } = useContactFiltersFields({
    labelPrefix: 'Adresát',
  });

  const createRef = useRef<ActionButtonHandle>(null);
  const editRef = useRef<ActionButtonHandle>(null);

  const contacts = useContacts();
  const contactSource = useScrollableSource({
    url: `${EvidenceApiUrl.SUBJECT_CONTACTS}/list`,
  });

  const concernedAuthorityTypes = useConcernedAuthorityTypes();

  const hasSubjectPermission =
    hasPermission(Role.HEAD_OSV) ||
    hasPermission(Role.LUSTRUM) ||
    hasPermission(Role.INDEX) ||
    hasPermission(Role.SECRETARY) ||
    hasPermission(Role.SOLVER) ||
    hasPermission(Role.ANONYMIZATION) ||
    hasPermission(Role.ANALYST) ||
    hasPermission(Role.PUBLICATION);

  return (
    <>
      <SubjectToolbar
        createRef={createRef}
        editRef={editRef}
        fieldName="contact.subject"
        participantMode={true}
        preselectContactId={getFieldValues()?.contact?.id}
        onResultValue={(subject: Subject) => {
          setFieldValue('contact', subject.selectedContact);
        }}
      />
      <FormDictionaryField
        name="contact"
        required={true}
        label={
          <FormattedMessage
            id="ES__ACCESSORY_PARTICIPANTS__FIELD_LABEL__SUBJECT"
            defaultMessage="Adresát"
          />
        }
        filters={contactFiltersFields}
        columns={contactColumns}
        autoFocus={true}
        autocompleteSource={contacts}
        dialogSource={contactSource}
        labelMapper={dictionaryFieldLabelMapper}
        maxRows={9}
        dialogWidth={1000}
        onCreate={() => createRef.current?.executeAction()}
        onEdit={() => editRef.current?.executeAction()}
        dialogSorts={CONTACTS_SORT}
        createRowStyle={createRowStyle}
        AutocompleteItemComponent={ContactAutocompleteItem}
        linkUrl={
          hasSubjectPermission
            ? (value: AutocompleteValue<Contact>) =>
                `${EvidenceBrowserUrl.SUBJECTS}/${value.single?.subject?.id}`
            : undefined
        }
      />
      <FormAutocomplete
        name="concernedAuthorityType"
        label={
          <FormattedMessage
            id="ES__ACCESSORY_PARTICIPANTS__FIELD_LABEL__CONCERNED_AUTHORITY_TYPE"
            defaultMessage="Typ dotčeného orgánu"
          />
        }
        labelMapper={autocompleteLabelMapper}
        source={concernedAuthorityTypes}
      />
    </>
  );
}
